import axios from 'axios'
import { AUTHORIZATION_HEADER, BASE_URL, ENDPOINTS, SOCKET_BASE_URL } from './endpoints'

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    Authorization: AUTHORIZATION_HEADER
  }
})

export const socketApiClient = axios.create({
  baseURL: SOCKET_BASE_URL
})

const appendValue = (formData, key, value) => {
  if (value === undefined || value === null) return
  formData.append(key, value)
}

export const toFormData = (payload = {}) => {
  if (payload instanceof FormData) return payload

  const formData = new FormData()
  Object.entries(payload).forEach(([key, value]) => appendValue(formData, key, value))
  return formData
}

const postForm = (endpoint, payload) => apiClient.post(endpoint, toFormData(payload))
const postSocketJson = (endpoint, payload) =>
  socketApiClient.post(endpoint, payload, {
    headers: { 'Content-Type': 'application/json' }
  })

const postMultipart = (endpoint, payload) =>
  apiClient.post(endpoint, toFormData(payload), {
    headers: { 'Content-Type': 'multipart/form-data' }
  })

const api = {
  login({ username = '', password = '' } = {}) {
    return postForm(ENDPOINTS.LOGIN, { username, password })
  },

  socialLogin(payload = {}) {
    return postForm(ENDPOINTS.SOCIAL_LOGIN, payload)
  },

  socialLoginCheck({ email = '' } = {}) {
    return postForm(ENDPOINTS.SOCIAL_LOGIN_CHECK, { email })
  },

  register(payload = {}) {
    return postForm(ENDPOINTS.REGISTER, payload)
  },

  getProfile({ id = '', userid = '' } = {}) {
    return postForm(ENDPOINTS.GET_PROFILE, { id, userid })
  },

  editProfile(payload = {}) {
    return postForm(ENDPOINTS.EDIT_PROFILE, payload)
  },

  editProfileImage(payload = {}) {
    return postMultipart(ENDPOINTS.EDIT_PROFILE, payload)
  },

  loginReset(payload = {}) {
    return postForm(ENDPOINTS.LOGIN_RESET, payload)
  },

  loginResetVerify(payload = {}) {
    return postForm(ENDPOINTS.LOGIN_RESET_VERIFY, payload)
  },

  changePassword({ id = '', oldpwd = '', newpwd = '' } = {}) {
    return postForm(ENDPOINTS.CHANGE_PASSWORD, { id, oldpwd, newpwd })
  },

  deleteAccount({ id = '' } = {}) {
    return postForm(ENDPOINTS.DELETE_ACCOUNT, { id })
  },

  updateTimezone({ id = '', timezone = '', zone = '' } = {}) {
    return postForm(ENDPOINTS.UPDATE_TIMEZONE, { id, timezone, zone })
  },

  liveAppsInviteApp({ type = 'user' } = {}) {
    return postForm(ENDPOINTS.LIVE_APPS_INVITEAPP, { type })
  },

  getBusinessList({ key = '', lat = '', long = '' } = {}) {
    return postForm(ENDPOINTS.GET_BUSINESS_LIST, { key, lat, long })
  },

  listReviewBusiness({ user_id = '' } = {}) {
    return postForm(ENDPOINTS.LIST_REVIEW_BUSINESS, { user_id })
  },

  listBusinessReviews({ business_id = '', user_id = '' } = {}) {
    return postForm(ENDPOINTS.LIST_BUSINESS_REVIEWS, { business_id, user_id })
  },

  listCheckins({ user_id = '' } = {}) {
    return postForm(ENDPOINTS.LIST_CHECKINS, { user_id })
  },

  nearbyFlyers({ lat = '', long = '', country = '', page = '', id = '', content_type = '', timezone = '' } = {}) {
    return postForm(ENDPOINTS.NEARBY_FLYERS, { lat, long, country, page, id, content_type, timezone })
  },

  searchFlyers({ lat = '', long = '', country = '', search_word = '', page = '', id = '' } = {}) {
    return postForm(ENDPOINTS.SEARCH_FLYERS, { lat, long, country, search_word, page, id })
  },

  hashtagFlyers({ id = '', hashtag = '' } = {}) {
    return postForm(ENDPOINTS.HASHTAG_FLYERS, { id, hashtag })
  },

  saveLocation({ lng = '', userid = '', location = '', lat = '', country_name = '', city_name = '', address = '', state_name = '' } = {}) {
    return postForm(ENDPOINTS.SAVE_LOCATION, { lng, userid, location, lat, country_name, city_name, address, state_name })
  },

  getLocations({ userid = '' } = {}) {
    return postForm(ENDPOINTS.GET_LOCATIONS, { userid })
  },

  getChatsFlyerUser({ userid = '' } = {}) {
    return postForm(ENDPOINTS.GET_CHATS_FLYER_USER, { userid })
  },

  getChatsFlyerUserView({ userid = '', to_id = '' } = {}) {
    return postForm(ENDPOINTS.GET_CHATS_FLYER_USER_VIEW, { userid, to_id })
  },

  sendChatFlyer({ userid = '', flyer_id = '', to_id = '', message = '' } = {}) {
    return postForm(ENDPOINTS.SEND_CHAT_FLYER, { userid, flyer_id, to_id, message })
  },

  sendSocketMessage({ userid = '', flyer_id = '', to_id = '', message = '' } = {}) {
    return postSocketJson(ENDPOINTS.SEND_SOCKET_MESSAGE, { userid, flyer_id, to_id, message })
  },

  savedFlyersUser({ page = '', user_id = '', board_id = '' } = {}) {
    return postForm(ENDPOINTS.SAVED_FLYERS_USER, { page, user_id, board_id })
  },

  listAddedFlyerLive({ page = '', user_id = '', username = '', login_user_id = '' } = {}) {
    return postForm(ENDPOINTS.LIST_ADDED_FLYER_LIVE, {
      page,
      user_id,
      username,
      login_user_id
    })
  },

  getFlyerContentTypes(payload = {}) {
    return postForm(ENDPOINTS.GET_FLYER_CONTENT_TYPES, payload)
  },

  getFlyerContentTypesSections(payload = {}) {
    return postForm(ENDPOINTS.GET_FLYER_CONTENT_TYPES_SECTIONS, payload)
  },

  detailFlyer({ flyer_id = '', user_id = '' } = {}) {
    return postForm(ENDPOINTS.DETAIL_FLYER, { flyer_id, user_id })
  },

  saveFlyer({ flyer_id = '', user_id = '', board_id = '' } = {}) {
    return postForm(ENDPOINTS.SAVE_FLYER, { flyer_id, user_id, board_id })
  },

  likeFlyer({ flyer_id = '', user_id = '' } = {}) {
    return postForm(ENDPOINTS.LIKE_FLYER, { flyer_id, user_id })
  },

  publishUnpublishFlyer({ id = '', status = '' } = {}) {
    return postForm(ENDPOINTS.PUBLISH_UNPUBLISH_FLYER, { id, status })
  },

  deleteFlyer({ flyer_id = '', flyer_type = '', user_id = '', business_id = '', checkin_date = '' } = {}) {
    return postForm(ENDPOINTS.FLYER_DELETE_USER, {
      flyer_id,
      flyer_type,
      user_id,
      business_id,
      checkin_date
    })
  },

  followUser({ follow_user_id = '', user_id = '' } = {}) {
    return postForm(ENDPOINTS.FLYER_FOLLOW_USER, { follow_user_id, user_id })
  },

  viewCommentsFlyer({ flyer_id = '' } = {}) {
    return postForm(ENDPOINTS.VIEW_COMMENTS_FLYER, { flyer_id })
  },

  addCommentFlyer({ flyer_id = '', user_id = '', comment = '', parent_id = '' } = {}) {
    return postForm(ENDPOINTS.ADD_COMMENT_FLYER, { flyer_id, user_id, comment, parent_id })
  },

  addFlyer(payload = {}) {
    return postMultipart(ENDPOINTS.ADD_FLYER, payload)
  },

  checkin({ userid = '', comments = '', business_name = '', business_adress = '', business_lat = '', business_long = '', location_name = '' } = {}) {
    return postForm(ENDPOINTS.CHECKIN, {
      userid,
      comments,
      business_name,
      business_adress,
      business_lat,
      business_long,
      location_name
    })
  },

  updateFlyer(payload = {}) {
    return postMultipart(ENDPOINTS.UPDATE_FLYER, payload)
  },

  listCategoryFlyer() {
    return postForm(ENDPOINTS.LIST_CATEGORY_FLYER)
  },

  listBoardsFlyer(payload = {}) {
    return postForm(ENDPOINTS.LIST_BOARDS_FLYER, payload)
  },

  listSavedFlyerBoards({ user_id = '' } = {}) {
    return postForm(ENDPOINTS.LIST_SAVED_FLYER_BOARDS, { user_id })
  },

  addBoardFlyer({ user_id = '', name = '' } = {}) {
    return postForm(ENDPOINTS.ADD_BOARD_FLYER, { user_id, name })
  },

  updateBoardFlyer({ id = '', name = '' } = {}) {
    return postForm(ENDPOINTS.UPDATE_BOARD_FLYER, { id, name })
  },

  login_reset(payload) {
    return this.loginReset(payload)
  },

  login_reset_verify(payload) {
    return this.loginResetVerify(payload)
  },

  changepwd(payload) {
    return postForm(ENDPOINTS.CHANGE_PASSWORD, payload)
  },

  deleteaccount(payload) {
    return postForm(ENDPOINTS.DELETE_ACCOUNT, payload)
  },

  update_timezone(payload) {
    return postForm(ENDPOINTS.UPDATE_TIMEZONE, payload)
  },

  social_login(payload) {
    return this.socialLogin(payload)
  },

  social_login_check(payload) {
    return postForm(ENDPOINTS.SOCIAL_LOGIN_CHECK, payload)
  },

  get_profile(payload) {
    return postForm(ENDPOINTS.GET_PROFILE, payload)
  },

  edit_profile(payload) {
    return postForm(ENDPOINTS.EDIT_PROFILE, payload)
  },

  nearby_flyers(payload) {
    return postForm(ENDPOINTS.NEARBY_FLYERS, payload)
  },

  search_flyers(payload) {
    return postForm(ENDPOINTS.SEARCH_FLYERS, payload)
  },

  savelocation(payload) {
    return postForm(ENDPOINTS.SAVE_LOCATION, payload)
  },

  get_locations(payload) {
    return postForm(ENDPOINTS.GET_LOCATIONS, payload)
  },

  get_chats_flyer_user(payload) {
    return postForm(ENDPOINTS.GET_CHATS_FLYER_USER, payload)
  },

  get_chats_flyer_user_view(payload) {
    return postForm(ENDPOINTS.GET_CHATS_FLYER_USER_VIEW, payload)
  },

  send_chat_flyer(payload) {
    return postForm(ENDPOINTS.SEND_CHAT_FLYER, payload)
  },

  send_message(payload) {
    return postSocketJson(ENDPOINTS.SEND_SOCKET_MESSAGE, payload)
  },

  saved_flyers_user(payload) {
    return postForm(ENDPOINTS.SAVED_FLYERS_USER, payload)
  },

  list_added_flyer_live(payload = {}) {
    return this.listAddedFlyerLive(payload)
  },

  get_flyer_content_types(payload = {}) {
    return this.getFlyerContentTypes(payload)
  },

  get_flyer_content_types_sections(payload = {}) {
    return this.getFlyerContentTypesSections(payload)
  },

  detail_flyer(payload) {
    return postForm(ENDPOINTS.DETAIL_FLYER, payload)
  },

  save_flyer(payload) {
    return postForm(ENDPOINTS.SAVE_FLYER, payload)
  },

  like_flyr(payload) {
    return postForm(ENDPOINTS.LIKE_FLYER, payload)
  },

  view_comments_flyer(payload) {
    return postForm(ENDPOINTS.VIEW_COMMENTS_FLYER, payload)
  },

  add_comment_flyer(payload) {
    return postForm(ENDPOINTS.ADD_COMMENT_FLYER, payload)
  },

  flyer_delete_user(payload) {
    return postForm(ENDPOINTS.FLYER_DELETE_USER, payload)
  },

  publish_unpublish_flyer(payload) {
    return postForm(ENDPOINTS.PUBLISH_UNPUBLISH_FLYER, payload)
  },

  flyer_follow_user(payload) {
    return postForm(ENDPOINTS.FLYER_FOLLOW_USER, payload)
  },

  add_flyer(payload) {
    return this.addFlyer(payload)
  },

  update_flyer(payload) {
    return this.updateFlyer(payload)
  },

  list_category_flyer(payload) {
    return postForm(ENDPOINTS.LIST_CATEGORY_FLYER, payload)
  },

  list_review_business(payload) {
    return postForm(ENDPOINTS.LIST_REVIEW_BUSINESS, payload)
  },

  list_checkins(payload = {}) {
    return this.listCheckins(payload)
  },

  list_boards_flyer(payload) {
    return postForm(ENDPOINTS.LIST_BOARDS_FLYER, payload)
  },

  list_saved_flyer_boards(payload) {
    return postForm(ENDPOINTS.LIST_SAVED_FLYER_BOARDS, payload)
  },

  add_board_flyer(payload) {
    return postForm(ENDPOINTS.ADD_BOARD_FLYER, payload)
  },

  update_board_flyer(payload) {
    return postForm(ENDPOINTS.UPDATE_BOARD_FLYER, payload)
  },

  loginApi(payload) {
    return postForm(ENDPOINTS.LOGIN, payload)
  },

  socialLoginApi(payload) {
    return postForm(ENDPOINTS.SOCIAL_LOGIN, payload)
  },

  registerApi(payload) {
    return postForm(ENDPOINTS.REGISTER, payload)
  },

  getProfileApi(payload) {
    return postForm(ENDPOINTS.GET_PROFILE, payload)
  },

  loginResetApi(payload) {
    return postForm(ENDPOINTS.LOGIN_RESET, payload)
  },

  loginResetVerifyApi(payload) {
    return postForm(ENDPOINTS.LOGIN_RESET_VERIFY, payload)
  },

  changePasswordApi(payload) {
    return postForm(ENDPOINTS.CHANGE_PASSWORD, payload)
  }
}

export default api
